import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getValueCookie } from '../../services/cookieService';
import { getInformationWithDataV3 } from '../../services/CABE';
import { AiOutlineFileText, AiOutlineReload } from 'react-icons/ai';

// Only render inside the Invoices pages (v2 and v3) — not across the whole
// app (login, company selection, other sections), per user feedback 2026-09-23.
const INVOICE_ROUTES = ['/invoices', '/invoices-v3'];

// Isolated component (2026-09-23) — does not touch any existing invoice logic.
// Shows the logged-in salesperson the real status of their own invoices from
// the last 24h (did it actually reach Sage, is it still retrying, or does it
// need manual attention?) so they don't have to redo them "just in case."
// The checker that fills this data runs on its own, on the backend
// (v3_controllers/invoiceMonitor.ts, deployed on backv3).

const POLL_MS = 30000;

const STATUS_LABEL = {
  pending: 'Pending verification',
  retrying: 'Retrying',
  confirmed: 'Confirmed in Sage',
  failed: 'Needs manual review',
  cancelled: 'Cancelled',
};

const STATUS_CLASS = {
  pending: 'secondary',
  retrying: 'warning',
  confirmed: 'success',
  failed: 'danger',
  cancelled: 'secondary',
};

// pending/retrying are still IN PROGRESS (spinner, not clickable) — confirmed/failed/cancelled
// are final states (static icon). A previous version used a plain "…" character for the
// in-progress icon, which looked like a clickable "more options" button but did nothing —
// per user feedback 2026-09-23, anything that looks interactive must either work or be removed.
const IN_PROGRESS_STATUSES = ['pending', 'retrying'];

function formatRelativeTime(dateStr) {
  if (!dateStr) return 'never';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffSec = Math.max(0, Math.round(diffMs / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  return `${diffHr}h ago`;
}

// Must match RECHECK_AFTER_MS in api-portal-manager/src/v3_controllers/invoiceMonitor.ts —
// an invoice only becomes eligible for its next check this many seconds after the last one.
// This is a floor, not a guarantee: if the backend's queue is backed up, the real check can
// land later, so this is labeled "earliest" rather than a promised time (user feedback
// 2026-09-23 — "the soon is of much help" [not helpful], a vague label was worse than a
// concrete-but-approximate one).
const RECHECK_AFTER_SECONDS = 60;

function getNextCheckLabel(inv) {
  if (!IN_PROGRESS_STATUSES.includes(inv.status)) return null; // no next check for a final state
  if (!inv.lastCheckedAt) return 'checking for the first time shortly';
  const secondsSinceChecked = Math.round((Date.now() - new Date(inv.lastCheckedAt).getTime()) / 1000);
  const remaining = RECHECK_AFTER_SECONDS - secondsSinceChecked;
  if (remaining <= 0) return 'eligible for next check now';
  return `earliest next check in ~${remaining}s`;
}

export const InvoiceMonitorButton = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [retryingKey, setRetryingKey] = useState(null);
  const intervalRef = useRef(null);

  const userName = getValueCookie('userName');
  const onInvoicesPage = INVOICE_ROUTES.includes(location.pathname);

  const fetchInvoices = useCallback(async () => {
    if (!userName) return;
    setLoading(true);
    try {
      const response = await getInformationWithDataV3('/v3/invoices/monitor/my-status', { userName });
      if (response && response.status && response.status.code === 1 && Array.isArray(response.data)) {
        setInvoices(response.data);
      }
    } catch (err) {
      console.log('[InvoiceMonitorButton] fetch error:', err);
    } finally {
      setLoading(false);
      setLastFetchedAt(new Date());
    }
  }, [userName]);

  useEffect(() => {
    if (!userName || !onInvoicesPage) return;
    fetchInvoices();
    intervalRef.current = setInterval(fetchInvoices, POLL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [userName, onInvoicesPage, fetchInvoices]);

  // Instant feedback the moment InvoiceV3.js finishes saving — do not wait for the backend's
  // own registration cycle or this component's own polling. Shows the invoice as "pending"
  // right away (optimistic — the real row may not exist in invoice_monitor for a few more
  // seconds), then reconciles with the real backend state shortly after. User feedback
  // 2026-09-23: "the user must realize their invoice is already being processed" — this is
  // the part that makes that true immediately, not eventually.
  useEffect(() => {
    if (!onInvoicesPage) return;
    const handleNewInvoice = (e) => {
      const { invoiceNo, idCompany } = e.detail || {};
      if (!invoiceNo) return;
      setInvoices((prev) => {
        if (prev.some((i) => i.invoiceNo === invoiceNo && i.idCompany === idCompany)) return prev;
        return [
          { invoiceNo, idCompany, status: 'pending', attempts: 0, lastCheckedAt: null, createdAt: new Date().toISOString() },
          ...prev,
        ];
      });
      setOpen(true); // surface the panel itself — a badge alone is easy to miss right after saving
      setTimeout(fetchInvoices, 8000); // reconcile with the real row once the backend has registered it
    };
    window.addEventListener('invoice-monitor:new-invoice', handleNewInvoice);
    return () => window.removeEventListener('invoice-monitor:new-invoice', handleNewInvoice);
  }, [onInvoicesPage, fetchInvoices]);

  if (!userName || !onInvoicesPage) return null;

  const needsAttention = invoices.filter(i => i.status === 'failed').length;
  const inProgress = invoices.filter(i => i.status === 'pending' || i.status === 'retrying').length;

  const togglePanel = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchInvoices(); // always refresh on open, never show stale data
  };

  const handleRetryNow = async (inv) => {
    const key = `${inv.invoiceNo}-${inv.idCompany}`;
    setRetryingKey(key);
    try {
      await getInformationWithDataV3('/v3/invoices/monitor/retry-now', {
        userName, invoiceNo: inv.invoiceNo, idCompany: inv.idCompany,
      });
      await fetchInvoices();
    } catch (err) {
      console.log('[InvoiceMonitorButton] retry-now error:', err);
    } finally {
      setRetryingKey(null);
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: 30, left: 30, zIndex: 9999 }}>
      <button className="btn btn-dark position-relative shadow" onClick={togglePanel}>
        <AiOutlineFileText size={22} /> Invoices Monitor

        {needsAttention > 0 && (
          <span className="badge bg-danger position-absolute top-0 start-100 translate-middle">
            {needsAttention}
          </span>
        )}
        {needsAttention === 0 && inProgress > 0 && (
          <span className="badge bg-warning text-dark position-absolute top-0 start-100 translate-middle">
            {inProgress}
          </span>
        )}
      </button>

      {open && (
        <div className="card shadow p-3 mb-2" style={{ width: 440, position: 'absolute', bottom: '110%', left: 0 }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Your invoices — last 24h</strong>
            <button
              type="button"
              className="btn btn-sm btn-primary d-flex align-items-center gap-1"
              onClick={fetchInvoices}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Loading...
                </>
              ) : (
                <>
                  <AiOutlineReload size={14} /> Refresh
                </>
              )}
            </button>
          </div>

          {lastFetchedAt && (
            <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
              Last updated: {lastFetchedAt.toLocaleTimeString()}
            </div>
          )}

          <ul className="list-group" style={{ maxHeight: 420, overflowY: 'scroll' }}>
            {loading && invoices.length === 0 ? (
              <li className="list-group-item text-center text-muted">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading...
              </li>
            ) : invoices.length === 0 ? (
              <li className="list-group-item text-muted">No invoices in the last 24h</li>
            ) : (
              invoices.map((inv) => {
                const key = `${inv.invoiceNo}-${inv.idCompany}`;
                const inProgress = IN_PROGRESS_STATUSES.includes(inv.status);
                return (
                  <li key={key} className="list-group-item d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold">{inv.invoiceNo}</div>
                      <small className={`text-${STATUS_CLASS[inv.status] || 'secondary'}`}>
                        {STATUS_LABEL[inv.status] || inv.status}
                        {inv.status === 'retrying' ? ` (attempt ${inv.attempts}/3)` : ''}
                      </small>
                      <br />
                      <small className="text-muted">Created: {new Date(inv.createdAt).toLocaleString()}</small>
                      <br />
                      <small className="text-muted">
                        Last checked: {formatRelativeTime(inv.lastCheckedAt)}
                        {inProgress ? ` · ${getNextCheckLabel(inv)}` : ''}
                      </small>

                      {inv.status === 'failed' && (
                        <div className="mt-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRetryNow(inv)}
                            disabled={retryingKey === key}
                          >
                            {retryingKey === key ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Retrying...
                              </>
                            ) : (
                              <>
                                <AiOutlineReload size={12} className="me-1" /> Retry now
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Static, non-clickable status icon — never a "..." menu look-alike (user feedback 2026-09-23).
                        Only shown for confirmed/failed, where a symbol actually adds information beyond the text
                        label — cancelled has no icon, the "Cancelled" label already says it (user feedback 2026-09-23). */}
                    {inProgress ? (
                      <span className="spinner-border spinner-border-sm text-warning align-self-center" role="status" aria-hidden="true"></span>
                    ) : inv.status === 'confirmed' ? (
                      <span className="badge bg-success align-self-center">✓</span>
                    ) : inv.status === 'failed' ? (
                      <span className="badge bg-danger align-self-center">!</span>
                    ) : null}
                  </li>
                );
              })
            )}
          </ul>

          <div className="text-muted mt-2" style={{ fontSize: '0.72rem', fontStyle: 'italic' }}>
            New invoices are checked automatically in the background — this can take a couple of minutes, it does not verify instantly.
          </div>

          {needsAttention > 0 && (
            <div className="alert alert-danger mt-2 mb-0 p-2">
              <small>{needsAttention} invoice(s) could not be confirmed automatically — contact support with the invoice number.</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceMonitorButton;
